import { useState } from 'react';
import { Calendar, Mail, Phone, MapPin, DollarSign, Music, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bookings = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: '',
    venue_location: '',
    budget_range: '',
    message: ''
  });

  const eventTypes = [
    'Club Night',
    'Private Party',
    'Wedding',
    'Corporate Event',
    'Festival',
    'Birthday Party',
    'Album Release',
    'Other'
  ];

  const budgetRanges = [
    'Under $500',
    '$500 - $1,000',
    '$1,000 - $2,500',
    '$2,500 - $5,000',
    '$5,000 - $10,000',
    '$10,000+'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.event_date || !formData.event_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/bookings`, formData);
      setSubmitted(true);
      toast.success('Booking request sent successfully!');
    } catch (error) {
      console.error('Failed to submit booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-20 px-6 flex items-center justify-center" data-testid="booking-success">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-[#D9F99D]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#D9F99D]" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Request Received!</h1>
          <p className="text-zinc-400 mb-6">
            Thank you for your booking request. DJ Shadow will review your inquiry and get back to you within 24-48 hours.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '', email: '', phone: '', event_date: '',
                event_type: '', venue_location: '', budget_range: '', message: ''
              });
            }}
            className="bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full px-6"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6" data-testid="bookings-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Book <span className="gradient-text">DJ Shadow</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Ready to bring the energy to your event? Fill out the form below and let's make it happen.
          </p>
        </div>

        {/* Booking Form */}
        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 space-y-6">
          {/* Contact Info Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2 text-[#D9F99D]">
              <Mail className="w-5 h-5" />
              Contact Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
                  data-testid="booking-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
                  data-testid="booking-email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10 bg-black/20 border-white/10 focus:border-[#D9F99D]"
                  data-testid="booking-phone"
                />
              </div>
            </div>
          </div>

          {/* Event Details Section */}
          <div className="space-y-4 pt-4 border-t border-white/10">
            <h2 className="text-lg font-bold flex items-center gap-2 text-[#D9F99D]">
              <Music className="w-5 h-5" />
              Event Details
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="event_type">Event Type *</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(val) => setFormData({ ...formData, event_type: val })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="booking-event-type">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    {eventTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_date">Event Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    className="pl-10 bg-black/20 border-white/10 focus:border-[#D9F99D]"
                    data-testid="booking-date"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue_location">Venue / Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="venue_location"
                  placeholder="Venue name and city"
                  value={formData.venue_location}
                  onChange={(e) => setFormData({ ...formData, venue_location: e.target.value })}
                  className="pl-10 bg-black/20 border-white/10 focus:border-[#D9F99D]"
                  data-testid="booking-venue"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="budget_range">Budget Range</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Select 
                  value={formData.budget_range} 
                  onValueChange={(val) => setFormData({ ...formData, budget_range: val })}
                >
                  <SelectTrigger className="pl-10 bg-black/20 border-white/10" data-testid="booking-budget">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    {budgetRanges.map((range) => (
                      <SelectItem key={range} value={range}>{range}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Message Section */}
          <div className="space-y-4 pt-4 border-t border-white/10">
            <h2 className="text-lg font-bold flex items-center gap-2 text-[#D9F99D]">
              <Send className="w-5 h-5" />
              Additional Details
            </h2>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Tell us more about your event, any special requirements, or questions..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-black/20 border-white/10 focus:border-[#D9F99D] min-h-[120px]"
                data-testid="booking-message"
              />
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] hover:shadow-[0_0_30px_-5px_rgba(217,249,157,0.6)] rounded-full py-6 text-lg font-bold transition-all duration-300"
            data-testid="booking-submit"
          >
            {loading ? (
              'Sending Request...'
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send Booking Request
              </>
            )}
          </Button>

          <p className="text-center text-zinc-500 text-sm">
            You'll receive a confirmation email and DJ Shadow will respond within 24-48 hours.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Bookings;
